/**
 * monthlyZeroFill - which months the Monthly Data table may render as $0.00.
 *
 * WHY THIS IS NOT JUST "fill every month in the year"
 * ---------------------------------------------------
 * The table zero-fills a calendar or financial year so the rows line up, and it
 * sorts month DESCENDING. A monthly aggregator file lands a month in arrears, so
 * the template always runs ahead of the data and the fabricated months sort to
 * the TOP. On 20 August 2026 My Mortgage Freedom's first view was Dec, Nov, Oct,
 * Sep and Aug 2026 all reading $0.00, above Jul 2026 at $600,882.35.
 *
 * Zero is a real value in this table: it means "invoiced nothing that month".
 * Rendering "not invoiced yet" as five months of collapse is a claim about the
 * tenant's book that the page cannot support.
 *
 * WHAT IS AND IS NOT CLAMPED
 *   - The TAIL is clamped: no month after the last month that carries data.
 *   - The HEAD is clamped the same way when the caller passes `earliest`: no
 *     month before the first month that carries data. Same defect at the other
 *     end of the spine, and the founder saw it in the financial-year view, where
 *     a tenant whose first data month is Aug 2025 led with a $0.00 "Jul 2025".
 *     A month before a tenant's first file is "not onboarded yet", not a month
 *     they invoiced nothing in.
 *   - A mid-year GAP still fills. A month the tenant genuinely skipped is a real
 *     zero and belongs in the table.
 *   - A window with NO data at all keeps the whole-year fill, rather than
 *     rendering an empty table on a page that has no empty state for it.
 *
 * Deliberately clock-free: the bound comes from the data, so it needs no
 * timezone decision and cannot drift from what the backend returned.
 */

/**
 * The latest 'YYYY-MM' present in the returned rows, or null when there are none.
 *
 * `yearMonth` is optional on the parameter because some series types declare it
 * so; the guard below is what makes that safe, not the signature.
 */
export function lastLoadedMonth(rows: ReadonlyArray<{ yearMonth?: string }>): string | null {
  let latest: string | null = null;
  for (const row of rows) {
    if (typeof row.yearMonth !== 'string' || row.yearMonth === '') continue;
    // 'YYYY-MM' is zero-padded, so lexicographic order is chronological order.
    if (latest === null || row.yearMonth > latest) latest = row.yearMonth;
  }
  return latest;
}

/** The earliest 'YYYY-MM' present in the returned rows, or null when there are none. */
export function firstLoadedMonth(rows: ReadonlyArray<{ yearMonth?: string }>): string | null {
  let earliest: string | null = null;
  for (const row of rows) {
    if (typeof row.yearMonth !== 'string' || row.yearMonth === '') continue;
    // 'YYYY-MM' is zero-padded, so lexicographic order is chronological order.
    if (earliest === null || row.yearMonth < earliest) earliest = row.yearMonth;
  }
  return earliest;
}

/**
 * May this month be added as a zero row?
 *
 * @param yearMonth   the candidate 'YYYY-MM'
 * @param latest      `lastLoadedMonth(rows)`; null means the window has no data
 *                    at all, in which case the previous whole-year fill stands.
 * @param earliest    `firstLoadedMonth(rows)`, when the caller also wants the
 *                    head clamped. Omitted (null) leaves leading months filling,
 *                    which is the tail-only behaviour callers had before.
 */
export function mayZeroFill(
  yearMonth: string,
  latest: string | null,
  earliest: string | null = null,
): boolean {
  if (latest === null) return true;
  if (earliest !== null && yearMonth < earliest) return false;
  return yearMonth <= latest;
}
